import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { GripVertical, Eye, Plus } from 'lucide-react';

import { DARK_BLACK } from 'constants/colours';
import { Question, QUESTION_TYPE_LABELS } from 'types/question.types';
import EditIcon from 'components/common/icons/EditIcon';
import { cn } from '@epoch-ui/lib/utils';
import { Button } from '@epoch-ui/components/Button';

import { FeedbackPreviewModal } from '../tabs/EditEventFeedback/components/FeedbackPreviewModal';
import { QuestionnairePreviewModal } from '../tabs/EditEventRegistration/components';

interface QuestionsListProps {
  questions: Question[];
  disabled: boolean;
  onPreview: (question: Question) => void;
  onEdit: (question: Question) => void;
}

const QuestionsList: React.FC<QuestionsListProps> = ({
  questions,
  disabled,
  onPreview,
  onEdit
}) => {
  return questions.map((question, index) => (
    <Draggable
      key={question.id || `question-${index}`}
      draggableId={String(question.id || `question-${index}`)}
      index={index}
      isDragDisabled={questions.length === 1 || disabled}
    >
      {(draggableProvided, snapshot) => (
        <div
          ref={draggableProvided.innerRef}
          {...draggableProvided.draggableProps}
          className={`tw-mt-3 tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid 
                    tw-border-border-grey tw-bg-background tw-py-4 tw-transition-shadow ${
                      snapshot.isDragging ? 'tw-shadow-lg' : ''
                    }`}
        >
          {/* Drag Handle */}
          <div
            {...draggableProvided.dragHandleProps}
            data-testid={`drag-handle-${question.id}`}
            className={`tw-ml-4 tw-cursor-move tw-text-muted-foreground ${
              questions.length === 1 || disabled ? 'tw-cursor-not-allowed tw-opacity-20' : ''
            }`}
          >
            <GripVertical size={16} className="tw-text-dark-black" />
          </div>

          {/* Question Content */}
          <button
            type="button"
            disabled={disabled}
            className="tw-mr-auto tw-min-w-0 tw-p-0 tw-text-left"
            onClick={() => onEdit(question)}
          >
            <h3 className="tw-mb-1 tw-break-words tw-text-sm tw-font-medium tw-leading-4 tw-text-dark-black">
              {question.label || 'Untitled question'}
            </h3>
            <div className="tw-text-xs tw-leading-4 tw-text-primary-grey">
              {QUESTION_TYPE_LABELS[question.type]} •{' '}
              {question.isRequired ? 'Required' : 'Optional'}
            </div>
          </button>

          {/* Edit Icon */}
          <div className="tw-mx-4 tw-cursor-pointer">
            {!disabled ? (
              <div data-testid={`edit-question-${question.id}`} onClick={() => onEdit(question)}>
                <EditIcon color={DARK_BLACK} />
              </div>
            ) : (
              <div
                data-testid={`preview-question-${question.id}`}
                className="tw-cursor-pointer"
                onClick={() => onPreview(question)}
              >
                <Eye size={16} className="tw-text-dark-black" />
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  ));
};

interface DraggableQuestionsListProps {
  disabled: boolean;
  questions: Question[];
  disableAddQuestion?: boolean;
  onReorder: (questions: Question[]) => void;
  onAddQuestion: () => void;
  onEditQuestion: (question: Question) => void;
}

export const DraggableQuestionsList: React.FC<DraggableQuestionsListProps> = ({
  disabled,
  questions,
  disableAddQuestion = false,
  onReorder,
  onAddQuestion,
  onEditQuestion
}) => {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);

  const handleQuestionPreview = useCallback((question: Question) => {
    setSelectedQuestion(question);
    setIsPreviewModalOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setIsPreviewModalOpen(false);
    setSelectedQuestion(null);
  }, []);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;

      if (sourceIndex === destinationIndex) {
        return;
      }

      const reorderedQuestions = Array.from(questions);
      const [movedQuestion] = reorderedQuestions.splice(sourceIndex, 1);
      reorderedQuestions.splice(destinationIndex, 0, movedQuestion);

      // Update sort values
      const updatedQuestions = reorderedQuestions.map((question, index) => ({
        ...question,
        sort: index
      }));

      onReorder(updatedQuestions);
    },
    [questions, onReorder]
  );

  if (questions.length === 0) {
    return null;
  }

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="questions">
          {(droppableProvided) => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              data-testid="draggable-questions-list"
              className={cn('tw-mt-4', disabled && 'tw-opacity-40')}
            >
              <QuestionsList
                questions={questions}
                disabled={disabled}
                onPreview={handleQuestionPreview}
                onEdit={onEditQuestion}
              />
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <Button
        size="xxs"
        className="tw-my-4 tw-gap-1 tw-rounded-xl tw-leading-4"
        disabled={disabled || disableAddQuestion}
        onClick={onAddQuestion}
      >
        <Plus size={16} />
        Add question
      </Button>

      <FeedbackPreviewModal
        open={!!selectedQuestion && isPreviewModalOpen && !selectedQuestion.shownBefore}
        onOpenChange={handleClosePreview}
        questions={[selectedQuestion]}
      />
      <QuestionnairePreviewModal
        open={!!selectedQuestion && isPreviewModalOpen && !!selectedQuestion.shownBefore}
        onOpenChange={handleClosePreview}
        questions={[selectedQuestion]}
      />
    </>
  );
};
